import React, { FC, useState, useEffect } from 'react';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import CSSTransition from 'react-transition-group/CSSTransition';
import Cookies from 'js-cookie';
import { loadApiData, loadExternalData } from '../../utils/ApiUtil';
import { getCurrentRuleSetName } from '../../utils/UrlUtil';
import { SystemNotification } from '../system-notification';
import { NotificationType } from '../system-notification/component';

export type Message = {
  id: string;
  view_locations?: string[];
  title?: string;
  level?: NotificationType;
  content?: any;
};

export interface Props {
  adminifiUrl: string;
  animate?: boolean;
  authToken?: string;
  messageRenderDelay?: number;
  messages?: Message[];
  userId: string;
}

export const SystemNotifications: FC<Props> = ({
  adminifiUrl,
  animate = true,
  authToken,
  messageRenderDelay = 250,
  messages,
  userId,
}) => {
  const getStorageKey = (id: string) => `${id}-closed-system-messages`;
  const closedMessageIds = (): string[] => {
    const cookie = Cookies.get(getStorageKey(userId));
    return cookie ? JSON.parse(cookie) : [];
  };

  const [notificationMessages, setNotificationMessages] = useState<Message[]>(messages || []);
  const [closedMessages, setClosedMessages] = useState<string[]>(closedMessageIds());

  useEffect(() => {
    if (!userId) {
      return;
    }
    const getUserMessages = async () => {
      const ruleSetName = getCurrentRuleSetName();
      const messagesFromApi = await getUserSytemNotifications(userId, adminifiUrl, authToken, ruleSetName);
      const allMessages = Array.isArray(messages) ? messages.concat(messagesFromApi) : messagesFromApi;
      const messagesToDisplay = allMessages.filter((message: Message) => !closedMessages.includes(message.id));
      if (messagesToDisplay.length) {
        if (animate) {
          setTimeout(() => {
            setNotificationMessages(messagesToDisplay);
          }, messageRenderDelay);
        } else {
          setNotificationMessages(messagesToDisplay);
        }
      }
    };
    getUserMessages();
  }, [userId, adminifiUrl, authToken, messages]);

  const getUserSytemNotifications = async (
    userId: string,
    adminifiUrl: string,
    authToken?: string,
    ruleSetName?: any
  ) => {
    const url: string = `${adminifiUrl}/api/data/user_system_messages/${userId}.json${
      ruleSetName ? `?rule_set_name=${ruleSetName}` : ''
    }`;

    let messages;
    try {
      if (authToken) {
        messages = await loadExternalData(url, authToken);
      } else {
        messages = await loadApiData(url);
      }
    } catch (e) {
      // if the BE errors, that's ok, we just won't show any messages
      messages = [];
    }

    return messages.filter((message: Message) => message?.view_locations?.includes('login'));
  };

  const getDomain = () => {
    const domainArray = window.location.host.split(':')[0].split('.');
    domainArray.shift();
    return domainArray.join('.');
  };

  const onClose = (messageId: string) => {
    const messages = notificationMessages.filter(message => message.id !== messageId);
    setNotificationMessages(messages);
    const updatedClosedMessages: string[] = [...closedMessages, messageId];
    Cookies.set(getStorageKey(userId), JSON.stringify(updatedClosedMessages), { domain: getDomain() });
    setClosedMessages(updatedClosedMessages);
  };

  const transitionTimeout = animate ? 1000 : 0;

  return (
    <div className="system-notifications inline">
      <TransitionGroup className="system-transition-group">
        {notificationMessages.map((message: Message) => {
          return (
            <CSSTransition classNames="notification" timeout={transitionTimeout} key={message.id}>
              <SystemNotification {...message} onClose={onClose} />
            </CSSTransition>
          );
        })}
      </TransitionGroup>
    </div>
  );
};
